import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, Star, ShieldCheck, Headset, Radio } from "lucide-react";
import { SplitReveal } from "@/components/motion/SplitReveal";
import { Reveal } from "@/components/motion/Reveal";
import { BRAND } from "@/data/content";

const PENDANT =
  "/products/fallguard-pro.jpg";
const HERO =
  "https://images.unsplash.com/photo-1758691031787-90867cb6fb2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGVsZGVybHklMjBjb3VwbGUlMjBhdCUyMGhvbWV8ZW58MHx8fHwxNzgyODUxNjkxfDA&ixlib=rb-4.1.0&q=85";

const chips = ["UL-Listed", "TMA Five Diamond", "100% US Operators", "No Contract"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 42]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-white" data-testid="hero-section">
      <div className="absolute top-0 right-0 h-full w-1/2 bg-dotgrid-navy opacity-70 pointer-events-none" />
      <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-brand-red/5 blur-3xl pointer-events-none" />

      <div className="relative container-x pt-14 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-10 items-center">
        {/* LEFT */}
        <div>
          <Reveal>
            <span className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-brand-bg border border-brand-line text-sm font-bold text-brand-navy">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-brand-red animate-pulseRing" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-red" />
              </span>
              Live operators standing by · 24/7
            </span>
          </Reveal>

          <h1 className="mt-6 font-display font-black tracking-tight text-brand-navy text-[2.75rem] sm:text-6xl lg:text-[4.4rem] leading-[0.98]">
            <SplitReveal
              lines={[
                "Independence,",
                <span key="2" className="text-brand-red">never alone.</span>,
              ]}
              delay={0.15}
            />
          </h1>

          <Reveal delay={0.5}>
            <p className="mt-6 text-lg sm:text-xl text-brand-ink2 leading-relaxed max-w-xl">
              Medical alert systems with automatic fall detection, GPS, and a real US-based
              person on the line in{" "}
              <span className="font-bold text-brand-navy">under 15 seconds</span>. Protection at
              home and everywhere you go.
            </p>
          </Reveal>

          <Reveal delay={0.62}>
            <div className="mt-8 flex flex-col sm:flex-row gap-3.5">
              <Link
                to="/shop"
                data-testid="hero-shop-button"
                className="btn-focus group inline-flex items-center justify-center gap-2 h-15 min-h-[60px] px-8 rounded-full bg-brand-red text-white font-bold text-lg hover:bg-brand-red-600 transition-colors shadow-red"
              >
                Find your device
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={2.6} />
              </Link>
              <a
                href={BRAND.phoneHref}
                data-testid="hero-call-button"
                className="btn-focus inline-flex items-center justify-center gap-2.5 h-15 min-h-[60px] px-8 rounded-full bg-white text-brand-navy font-bold text-lg border-2 border-brand-navy hover:bg-brand-navy hover:text-white transition-colors"
              >
                <Phone className="w-5 h-5" strokeWidth={2.6} />
                {BRAND.phone}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.74}>
            <div className="mt-9 flex flex-wrap items-center gap-x-5 gap-y-3">
              <div className="flex items-center gap-1.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-brand-red text-brand-red" />
                ))}
                <span className="ml-1.5 font-bold text-brand-navy">{BRAND.rating} / 5</span>
              </div>
              <span className="h-4 w-px bg-brand-line" />
              <span className="text-brand-ink2 font-semibold">
                {BRAND.familiesServed} families protected
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.82}>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {chips.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-brand-bg border border-brand-line text-sm font-semibold text-brand-navy"
                >
                  <ShieldCheck className="w-4 h-4 text-brand-red" />
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        {/* RIGHT */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <motion.div
            style={{ y: imgY }}
            className="relative rounded-[2rem] overflow-hidden shadow-cardHover ring-1 ring-brand-line"
          >
            <img src={HERO} alt="Senior couple enjoying life at home, safe and protected" className="w-full h-[440px] lg:h-[560px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/35 via-transparent to-transparent" />
          </motion.div>

          {/* floating operator card */}
          <motion.div
            style={{ y: cardY }}
            className="absolute -left-4 lg:-left-8 bottom-8 w-64 rounded-2xl bg-white shadow-cardHover border border-brand-line p-4"
          >
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-brand-navy shrink-0">
                <Headset className="w-5 h-5 text-brand-red" strokeWidth={2.4} />
              </span>
              <div>
                <p className="font-display font-bold text-brand-navy leading-tight">Operator online</p>
                <p className="text-sm text-brand-ink2">Answering in &lt; 15 sec</p>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs font-bold text-emerald-600">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 animate-ping opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              CONNECTED · US-BASED
            </div>
          </motion.div>

          {/* floating pendant */}
          <div className="absolute -right-3 lg:-right-6 -top-4 w-32 lg:w-40 rounded-2xl bg-white shadow-cardHover border border-brand-line p-2 animate-float">
            <img src={PENDANT} alt="Waterproof help button pendant" className="w-full h-full object-contain" />
            <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-brand-red text-white text-[11px] font-bold whitespace-nowrap">
              Press for help
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
